import { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { toast } from 'react-toastify'
import {
  Table,
  TableContainer,
  TableCell,
  TextField,
  TableHead,
  TableRow,
  TableBody,
  Tooltip,
  Paper,
  IconButton,
  Typography,
  CircularProgress,
  Box,
  MenuItem,
  Select,
} from '@mui/material'
import { AddOutlined as AddOutlinedIcon, Refresh } from '@mui/icons-material'
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuthContext } from '../../hooks/useAuthContext'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import {
  ACTIVITY_STATUSES_DASHBOARD,
  DROPDOWN_DEFAULT_TEXT,
  DYNAMIC_FIELD_TYPE,
  FORMATS,
  PROCESS_ACTIVITY_STATUSES,
  PROCESS_STATUSES,
} from '../../utility/constants'
import {
  ACTIVITY_TYPES,
  CURRENCY_COUNTRY_CODE,
  DROPDOWN_DEFAULT_VALUE,
  EXCEL_COLUMNS,
  FIELD_TYPES,
  REDIRECT_TIMER,
  TOAST_SETTINGS,
  TOAST_SETTINGS_ERROR,
} from '../../utility/constants'
import {
  getProcessDashboardReportData,
  getProcessStatusByReferenceNum,
} from '../../utility/dataEntry/formData'
import ProcessDashboardValues from '../../components/Dashboard/ProcessDashboardValues'
import { intersection } from '../../utility/utilityFunctions'
import ConfirmationBox from '../../components/Dashboard/ConfirmationBox'
import moment from 'moment'
import { containsKeys } from '../DataEntryPage/utils/filterUtils'
import { getFieldId } from '../DataEntryPage/utils/utils'
import { downloadExcel } from '../../utility/excelDownloadUtils'
import TableSetting from '../../components/Dashboard/PreviewComponent/TableSettings/TableSetting'
import { getColumnForSetting, getFieldForSetting } from '../DataEntryPage/utils/fieldUtil'
import { userConfig } from '../../services/userConfigServices'
import DateRangePickerComponent from '../../components/Dashboard/DateRangePickerComponent'
import DateTimeRangePickerComponent from '../../components/Dashboard/DateTimeRangePickerComponent'
import NumberRangeComponent from '../../components/Dashboard/NumberRange'
import ListDropdown from '../../components/Dashboard/ListRange'
import CurrencyRangeComponent from '../../components/Dashboard/CurrencyRangeComponent'
import ClearableTextField from '../../components/Dashboard/ClearableTextField'
import { useResponsiveTypography } from '../../utility/responsiveTypography'
import { TimeCalculationCategories } from '../../utility/constants'
import { minutesToHours } from 'date-fns'
import { calculateEffectiveMinutes } from '../../utility/processDashboard/workHourCalculation'

const ProcessDashboard = () => {
  const searchTimer = useRef()
  const { user } = useAuthContext()
  const navigate = useNavigate()
  const NODE_BASE_URL = process.env.REACT_APP_NODE_BASE_URL
  const ASSIGNEE_DROPDOWN_DEFAULT_TEXT = 'All Assignees'
  const confirmationText = 'Are you sure you want to create a new process workflow?'
  let axiosOptions = {}
  if (user) {
    axiosOptions = {
      headers: { Authorization: `Bearer ${user.token}` },
    }
  }
  const [processDashBoardData, setProcessDashBoardData] = useState({
    processTemplate: {},
    processData: [],
    fieldTemplatesAll: [],
    fieldTemplates: [],
    hiddenColumns: [],
    activityTemplatesAll: [],
    activityTemplates: [],
    hiddenActivityColumns: [],
  })
  const [filteredProcessData, setFilteredProcessData] = useState([])
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isCreateNewProcessButtonDisabled, setIsCreateNewProcessButtonDisabled] = useState(false)
  const [isCreateNewProcessButtonHidden, setIsCreateNewProcessButtonHidden] = useState(true)
  const [currencyForDashboardValues, setCurrencyForDashboard] = useState([])
  const [searchFields, setSearchFields] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [excelLoading, setExcelLoading] = useState(false)
  const [showFilterIcons, setShowFilterIcons] = useState(false)
  const [clearFilterInput, setClearFilterInput] = useState({})
  const [stickyRowTop, setStickyRowTop] = useState(0)
  const [secondHeaderTop, setSecondHeaderTop] = useState(0)
  const [filterRowTop, setFilterRowTop] = useState(0)
  const [totalProcessCreated, setTotalProcessCreated] = useState(0)
  const tableHeadRef = useRef(null)
  const tableRef = useRef(null)
  const [processDataForProcessTemplate, setProcessDataForProcessTemplate] = useState([])
  const [loadAllData, setLoadAllData] = useState(false)
  const [loadAllDataWarningDialog, setLoadAllDataWarningDialog] = useState(false)
  const [companyDetails, setCompanyDetails] = useState({})
  const [usersList, setUsersList] = useState([])
  const [aggregatedProcessStatus, setAggregatedProcessStatus] = useState([])
  const [totalTimeTaken, setTotalTimeTaken] = useState([])
  const [timeStatType, setTimeStatType] = useState(TimeCalculationCategories.AVERAGE)
  const [activityStatType, setActivityStatType] = useState(TimeCalculationCategories.AVERAGE)
  const [selectedActivity, setSelectedActivity] = useState(null)
  const [selectedUser, setSelectedUser] = useState(ASSIGNEE_DROPDOWN_DEFAULT_TEXT)
  const [modifiedByUsers, setModifiedByUsers] = useState([])
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const getAlignmentForType = (type) => {
    const rightTypes = ['date', 'currency', 'number', 'datetime']
    return rightTypes.includes(type) ? 'right' : 'left'
  }
  const {
    processTemplate,
    processData,
    fieldTemplatesAll,
    fieldTemplates,
    hiddenColumns,
    activityTemplatesAll,
    activityTemplates,
    hiddenActivityColumns,
  } = processDashBoardData
  const processTemplateId = useParams().process_template_id
  const isFiltersOn = Object.keys(searchFields)?.length > 0 ? true : false
  const LOAD_ALL_DATA_LIMIT = 200

  const { dashboardTittle, cardIcons } = useResponsiveTypography()
  const defaultTableHeaders = [
    { id: 1001, name: 'Reference Number', width: '7rem', data_structure: FIELD_TYPES.TEXT },
    { id: 1002, name: 'Status', width: '3rem', data_structure: FIELD_TYPES.LIST },
    { id: 1003, name: 'Process Initiated On', width: '6rem', data_structure: FIELD_TYPES.DATE },
    { id: 1004, name: 'Process Closed On', width: '6rem', data_structure: FIELD_TYPES.DATE },
    { id: 1005, name: 'Current Activity', width: '8rem', data_structure: FIELD_TYPES.LIST },
    { id: 1006, name: 'Current Assignee', width: '8rem', data_structure: FIELD_TYPES.LIST },
    { id: 1007, name: 'Activity Assigned On', width: '6rem', data_structure: FIELD_TYPES.DATE },
    {
      id: 1008,
      name: 'Time Taken (Work Days)',
      width: '7.5rem',
      data_structure: FIELD_TYPES.NUMBER,
    },
  ]

  const defaultActivityTableHeaders = [
    { id: 2001, name: 'Version', width: '7rem', data_structure: FIELD_TYPES.NUMBER },
    { id: 2002, name: 'Status', width: '3rem', data_structure: FIELD_TYPES.LIST },
    { id: 2006, name: 'Activity Assigned On', width: '6rem', data_structure: FIELD_TYPES.DATE },
    { id: 2003, name: 'Closed Date', width: '6rem', data_structure: FIELD_TYPES.DATE },
    { id: 2004, name: 'Assignee', width: '6rem', data_structure: FIELD_TYPES.LIST },
    { id: 2005, name: 'Work hours', width: '8rem', data_structure: FIELD_TYPES.NUMBER },
  ]
  const grayHeaderColor = '#f7f9ff'
  useEffect(() => {
    const updateStickyPositions = () => {
      if (tableHeadRef.current) {
        const rows = tableHeadRef.current.querySelectorAll('tr')
        const firstRowHeight = rows[0]?.offsetHeight || 0
        const secondRowHeight = rows[1]?.offsetHeight || 0
        setStickyRowTop(firstRowHeight)
        setSecondHeaderTop(firstRowHeight)
        setFilterRowTop(firstRowHeight + secondRowHeight)
      }
    }

    updateStickyPositions()

    // Re-measure on resize too
    const observer = new ResizeObserver(updateStickyPositions)
    if (tableHeadRef.current) {
      observer.observe(tableHeadRef.current)
    }
    return () => observer.disconnect()
  }, [
    tableHeadRef.current,
    defaultTableHeaders,
    fieldTemplatesAll,
    activityTemplatesAll,
    showFilterIcons,
  ])

  const handleCreateNewProcess = async () => {
    setIsDialogOpen(false)
    setIsCreateNewProcessButtonDisabled(true)
    const toastErrorMsg = 'An error occurred while initiating the new process.'
    const toastSuccessMsg = 'New process initiated successfully.'
    try {
      const { process_id, activity_template_id } = (
        await axios.post(
          `${NODE_BASE_URL}/data-entry/process-data`,
          { process_template_id: processTemplateId },
          axiosOptions,
        )
      ).data

      toast.success(toastSuccessMsg, TOAST_SETTINGS)
      setTimeout(
        () => navigate(`/process-data/${process_id}/${activity_template_id}`),
        REDIRECT_TIMER,
      )
    } catch (err) {
      toast.error(toastErrorMsg, TOAST_SETTINGS_ERROR)
      setIsCreateNewProcessButtonDisabled(false)
    }
  }

  const getActivityData = (processReferenceNum, allProcessData, activityId, holidayList) => {
    const filteredActivityData = allProcessData
      .filter(
        (pd) =>
          pd.process_reference_num == processReferenceNum && pd.activity_template_id == activityId,
      )
      .sort((a, b) => a.id - b.id)

    if (filteredActivityData.length > 0) {
      const latestData = filteredActivityData[filteredActivityData.length - 1]
      let assigneeName = latestData.modified_by_user || ''

      const totalWorkMinutes = filteredActivityData.reduce((total, activity) => {
        const startTime = new Date(activity.created_at)
        const endTime =
          activity.status === PROCESS_ACTIVITY_STATUSES.ACTIVE || !activity.modified_at
            ? new Date()
            : new Date(activity.modified_at)
        return total + calculateEffectiveMinutes(startTime, endTime, holidayList)
      }, 0)

      const totalWorkHours = Number(minutesToHours(totalWorkMinutes).toFixed(2))

      return {
        version: latestData.version,
        status:
          latestData.status?.toLowerCase() === PROCESS_ACTIVITY_STATUSES.ACTIVE
            ? ACTIVITY_STATUSES_DASHBOARD.ACTIVE
            : latestData.status
              ? ACTIVITY_STATUSES_DASHBOARD.CLOSED
              : '',
        assignedAt: latestData?.created_at ?? '',
        closedDate:
          latestData.status === PROCESS_ACTIVITY_STATUSES.ACTIVE
            ? ''
            : latestData.modified_at || '',
        assignee: assigneeName,
        workHours: totalWorkHours,
      }
    }

    return {
      version: '',
      status: '',
      assignedAt: '',
      closedData: '',
      assignee: '',
      workHours: null,
    }
  }

  const defaultActivity = activityTemplatesAll[0]?.activity_template_name ?? null
  const isAnalyticsActive =
    selectedUser !== ASSIGNEE_DROPDOWN_DEFAULT_TEXT ||
    selectedActivity !== defaultActivity ||
    activityStatType !== TimeCalculationCategories.AVERAGE

  /* search functionality implementation */
  useEffect(() => {
    if (searchTimer.current) {
      clearTimeout(searchTimer.current)
    }

    searchTimer.current = setTimeout(() => {
      let arr = []
      Object.keys(searchFields).forEach((sfKey, i) => {
        const index = Number(sfKey.split('_')[1])
        const filteredProcessData = processData?.filter((pd) => {
          const cell = pd[index + 1]
          if (cell) {
            // Handle currency fields
            if (cell.fieldType === FIELD_TYPES.CURRENCY) {
              const cellCurrencyObject = currencyForDashboardValues.find(
                (currency) => currency.process_reference_num == cell.process_reference_num,
              )
              return containsKeys(
                cell.value,
                searchFields[sfKey],
                cell.fieldType,
                cellCurrencyObject,
              )
            }
            // Handle array fields (currentTask, currentAssignee)
            else if (Array.isArray(cell.value)) {
              const searchValues = searchFields[sfKey]
              // Check if any search value matches any cell value
              return searchValues.some((searchVal) =>
                cell.value.some((cellVal) => String(cellVal).trim() === String(searchVal).trim()),
              )
            }
            // Handle regular fields
            else {
              return containsKeys(cell.value, searchFields[sfKey], cell.fieldType)
            }
          }
          return false
        })
        arr[i] = filteredProcessData
      })

      let atLeastOneIsEmpty = false
      for (let i = 0; i < arr.length; i++) {
        if (arr[i].length === 0) {
          atLeastOneIsEmpty = true
          break
        }
      }
      if (atLeastOneIsEmpty) {
        setFilteredProcessData([])
      } else {
        const ints = intersection(arr)
        setFilteredProcessData(ints)
      }
      searchTimer.current = null
    }, 200)
  }, [searchFields, processData, currencyForDashboardValues])

  //  getting data by processTemplateId
  useEffect(() => {
    if (processTemplateId != null && user) {
      ;(async () => {
        await getDataFromServer(LOAD_ALL_DATA_LIMIT)
      })()
    }
  }, [processTemplateId, user])

  useEffect(() => {
    if (isAnalyticsActive) {
      setTooltipOpen(true)
    } else {
      setTooltipOpen(false)
    }
  }, [isAnalyticsActive])

  const getUserProcessDashboardConfig = (processTemplateId) => {
    try {
      const configs = JSON.parse(localStorage.getItem('processDashboardConfigs') || '[]')

      return configs.find((item) => item.process_template_id === processTemplateId)
    } catch (e) {
      console.error('Error reading localStorage', e)
      return null
    }
  }

  useEffect(() => {
    if (activityTemplatesAll.length > 0) {
      setSelectedActivity(activityTemplatesAll[0].activity_template_name)
    }
    if (selectedUser !== ASSIGNEE_DROPDOWN_DEFAULT_TEXT)
      setSelectedUser(ASSIGNEE_DROPDOWN_DEFAULT_TEXT)
    if (timeStatType !== TimeCalculationCategories.AVERAGE)
      setTimeStatType(TimeCalculationCategories.AVERAGE)
  }, [searchFields])

  const getDataFromServer = async (limit) => {
    setIsLoading(true)

    if (limit === LOAD_ALL_DATA_LIMIT) {
      toast.warning('Limited to 200 most recent records on initial page load.', {
        ...TOAST_SETTINGS,
        toastId: 'load-limit-warning',
      })
    }

    try {
      const processRelatedData = (
        await axios.get(
          `${NODE_BASE_URL}/process-dashboard/${processTemplateId}?limit=${limit}`,
          axiosOptions,
        )
      ).data

      const processTemplateResult = processRelatedData.processTemplate
      setModifiedByUsers([
        ...new Set(processRelatedData.processData.map((mb) => mb.modified_by_user).filter(Boolean)),
      ])

      const listDataForCompanyResult = processRelatedData.listData
      const usersAndTeamsDynamicList = processRelatedData.dynamicListData
      setUsersList(usersAndTeamsDynamicList?.usersList || [])
      let fieldTemplatesResult = processRelatedData.processDashboardFields
      const processDataForProcessTemplate = processRelatedData.processData
      const processInternalVisibility = processRelatedData.processInternalVisibility
      const isViewAllowed = processRelatedData.isViewAllowed
      const isInitiateAllowed = processRelatedData.isInitiationAllowed
      const currencyForDashboard = processRelatedData.nonDefaultCurrencies
      const activityTemplates = processRelatedData.activityTemplates
        .filter(
          (activity) =>
            [
              ACTIVITY_TYPES.DATA_ENTRY,
              ACTIVITY_TYPES.REVIEW,
              ACTIVITY_TYPES.LONG_RUNNING,
            ].includes(activity.activity_type) &&
            activity.activity_type !== ACTIVITY_TYPES.PARALLEL_START &&
            activity.activity_type !== ACTIVITY_TYPES.PARALLEL_END,
        )
        .map((activity) => ({ ...activity, is_hidden: false }))

      const holidayList = processRelatedData.holidays
      const totalProcessCreated = processRelatedData.totalProcessCreated
      setCurrencyForDashboard(currencyForDashboard)
      setCompanyDetails(processRelatedData.companyDetails)

      setIsCreateNewProcessButtonHidden(!isInitiateAllowed)
      setTotalProcessCreated(totalProcessCreated)

      if (!isViewAllowed) {
        navigate(`/process-dashboard/`)
      }
      let processDataObj = {}
      processDataForProcessTemplate.forEach((pdRow) => {
        for (const [key, value] of Object.entries(pdRow.data)) {
          // we don't need the table subfields here
          if (!key.includes('__')) {
            processDataObj[key + '~' + pdRow.process_reference_num] = value
          }
        }
      })

      let processDataArr = []
      // after the following loop, the index of processDataArr will be the process ref num
      for (const [key, value] of Object.entries(processDataObj)) {
        const [keyWithoutRefNum, processReferenceNum] = key.split('~')
        const index = parseInt(processReferenceNum)
        if (!processDataArr[index]) {
          processDataArr[index] = {}
        }
        processDataArr[index][keyWithoutRefNum] = value
      }
      let checkProcessDataIsVisibleForTheUser = (processData) => {
        let returnValue = true
        if (processInternalVisibility?.length > 0) {
          let currentUserTeams = user?.teams
          Object.keys(processData).forEach((key) => {
            let fieldId = key.split('__')[0].split('_')[1]
            processInternalVisibility?.forEach((piv) => {
              if (
                fieldId == piv?.is_private_field_template_id &&
                processData[key] == piv?.is_private_list_data_id
              ) {
                let allowedTeams =
                  processData[getFieldId(piv?.private_teams_list_field_template_id)]

                if (Array.isArray(allowedTeams)) {
                  returnValue = !allowedTeams.some((val) => currentUserTeams?.includes(val))
                } else {
                  returnValue = !currentUserTeams.includes(allowedTeams)
                }
              }
            })
          })
        }
        return returnValue
      }

      // now we make the internal object an array in the same order as the fields

      let processDataArr2 = []
      let aggregatedProcessStatusLocal = []
      let totalTimeTakenLocal = []

      for (let i = 0; i < processDataArr.length; i++) {
        if (processDataArr[i] == null) {
          continue
        }

        if (!checkProcessDataIsVisibleForTheUser(processDataArr[i])) {
          continue
        }

        const processStatus = getProcessStatusByReferenceNum(i, processDataForProcessTemplate)
        aggregatedProcessStatusLocal.push(processStatus)

        setProcessDataForProcessTemplate(processDataForProcessTemplate)

        const processInitiatedAt = getProcessDashboardReportData(
          i,
          processDataForProcessTemplate,
          'initiatedAt',
        )

        const processClosedAt = getProcessDashboardReportData(
          i,
          processDataForProcessTemplate,
          'closedAt',
        )

        const currentTask = getProcessDashboardReportData(
          i,
          processDataForProcessTemplate,
          'currentTask',
        )
        const currentAssignee = getProcessDashboardReportData(
          i,
          processDataForProcessTemplate,
          'currentAssignee',
        )
        const TaskAssignedAt = getProcessDashboardReportData(
          i,
          processDataForProcessTemplate,
          'taskAssignedAt',
        )
        const calenderTime = getProcessDashboardReportData(
          i,
          processDataForProcessTemplate,
          'workingTime',
          holidayList,
        )
        totalTimeTakenLocal.push(calenderTime)

        let processDataRow = [
          //1: Process ref
          {
            value: processTemplateResult[0].prefix + '-' + i,
            fieldType: null,
            is_hidden: false,
            process_reference_num: i,
          },
          //2: Process status
          {
            value: processStatus,
            fieldType: null,
            is_hidden: false,
            process_reference_num: i,
          },
          //3: Process initiate at
          {
            value: processInitiatedAt,
            fieldType: 'date',
            is_hidden: false,
            process_reference_num: i,
          },
          //4 : Process Closed at
          {
            value: processClosedAt,
            fieldType: 'date',
            is_hidden: false,
            process_reference_num: i,
          },
          //5 : Current Task - STORE AS ARRAY, NOT JSX
          {
            value: Array.isArray(currentTask) ? currentTask : [],
            fieldType: 'list', // Changed from 'text' to 'list'
            is_hidden: false,
            process_reference_num: i,
          },
          //6 : Current Assignee - STORE AS ARRAY, NOT JSX
          {
            value: Array.isArray(currentAssignee) ? currentAssignee : [],
            fieldType: 'list', // Changed from 'text' to 'list'
            is_hidden: false,
            process_reference_num: i,
          },
          //7 : Task Assigned At
          {
            value: TaskAssignedAt,
            fieldType: 'date',
            is_hidden: false,
            process_reference_num: i,
          },
          //8 : Time Taken
          {
            value: calenderTime,
            fieldType: 'number',
            is_hidden: false,
            process_reference_num: i,
          },
        ]

        const getListValue = (list, currentListId, is_hidden, id) => {
          const listData = list.find((it) => it.id == currentListId)
          processDataRow.push({
            value: listData?.value ? listData?.value : '',
            fieldType: FIELD_TYPES.LIST,
            is_hidden: is_hidden,
            id: id,
            process_reference_num: i,
          })
        }

        for (let j = 0; j < fieldTemplatesResult.length; j++) {
          if (fieldTemplatesResult[j].data_structure === FIELD_TYPES.LIST) {
            if (
              processDataArr[i]['f_' + fieldTemplatesResult[j].id] == DROPDOWN_DEFAULT_VALUE ||
              processDataArr[i]['f_' + fieldTemplatesResult[j].id] == undefined
            ) {
              processDataRow.push({
                value: '',
                fieldType: FIELD_TYPES.LIST,
                is_hidden: fieldTemplatesResult[j].is_hidden,
                id: fieldTemplatesResult[j].id,
                process_reference_num: i,
              })
            } else {
              if (fieldTemplatesResult[j].dynamic_field_type != null) {
                const meta = fieldTemplatesResult[j].dynamic_field_type
                if (meta?.type == DYNAMIC_FIELD_TYPE.TEAMS) {
                  let teamsList = usersAndTeamsDynamicList.teamsList
                  getListValue(
                    teamsList,
                    processDataArr[i]['f_' + fieldTemplatesResult[j].id],
                    fieldTemplatesResult[j].is_hidden,
                    fieldTemplatesResult[j].id,
                  )
                } else if (meta?.type == DYNAMIC_FIELD_TYPE.USERS) {
                  let usersList = usersAndTeamsDynamicList.usersList
                  getListValue(
                    usersList,
                    processDataArr[i]['f_' + fieldTemplatesResult[j].id],
                    fieldTemplatesResult[j].is_hidden,
                    fieldTemplatesResult[j].id,
                  )
                } else {
                  console.error('Dynamic Field Implementation Pending for ' + meta?.type)
                }
              } else {
                getListValue(
                  listDataForCompanyResult,
                  processDataArr[i]['f_' + fieldTemplatesResult[j].id],
                  fieldTemplatesResult[j].is_hidden,
                  fieldTemplatesResult[j].id,
                )
              }
            }
          } else if (fieldTemplatesResult[j].data_structure === FIELD_TYPES.LIST_MULTI) {
            if (
              processDataArr[i]['f_' + fieldTemplatesResult[j].id] == DROPDOWN_DEFAULT_VALUE ||
              processDataArr[i]['f_' + fieldTemplatesResult[j].id] == undefined
            ) {
              processDataRow.push({
                value: '',
                fieldType: FIELD_TYPES.LIST_MULTI,
                is_hidden: fieldTemplatesResult[j].is_hidden,
                id: fieldTemplatesResult[j].id,
                process_reference_num: i,
              })
            } else {
              if (fieldTemplatesResult[j].dynamic_field_type != null) {
                const meta = fieldTemplatesResult[j].dynamic_field_type
                if (meta?.type == DYNAMIC_FIELD_TYPE.TEAMS) {
                  let teamsList = usersAndTeamsDynamicList.teamsList
                  if (Array.isArray(processDataArr[i]['f_' + fieldTemplatesResult[j].id])) {
                    let val = []
                    processDataArr[i]['f_' + fieldTemplatesResult[j].id].forEach((ele) => {
                      const listData = teamsList.find((it) => it.id == ele)
                      if (listData) {
                        val.push(listData.value)
                      }
                    })
                    processDataRow.push({
                      value: val,
                      fieldType: FIELD_TYPES.LIST_MULTI,
                      is_hidden: fieldTemplatesResult[j].is_hidden,
                      id: fieldTemplatesResult[j].id,
                      process_reference_num: i,
                    })
                  }
                } else if (meta?.type == DYNAMIC_FIELD_TYPE.USERS) {
                  let usersList = usersAndTeamsDynamicList.usersList
                  if (Array.isArray(processDataArr[i]['f_' + fieldTemplatesResult[j].id])) {
                    let val = []
                    processDataArr[i]['f_' + fieldTemplatesResult[j].id].forEach((ele) => {
                      const listData = usersList.find((it) => it.id == ele)
                      if (listData) {
                        const nameOnly = listData.value.split('(')[0].trim()
                        val.push(nameOnly)
                      }
                    })
                    processDataRow.push({
                      value: val,
                      fieldType: FIELD_TYPES.LIST_MULTI,
                      is_hidden: fieldTemplatesResult[j].is_hidden,
                      id: fieldTemplatesResult[j].id,
                      process_reference_num: i,
                    })
                  }
                } else {
                  console.error('Dynamic Field Implementation Pending for ' + meta?.type)
                }
              } else {
                if (Array.isArray(processDataArr[i]['f_' + fieldTemplatesResult[j].id])) {
                  let val = []
                  processDataArr[i]['f_' + fieldTemplatesResult[j].id].forEach((ele) => {
                    const res = listDataForCompanyResult.find((lst) => lst.id == ele)
                    if (res) {
                      val.push(res?.value)
                    }
                  })
                  if (val) {
                    processDataRow.push({
                      value: val,
                      fieldType: FIELD_TYPES.LIST_MULTI,
                      is_hidden: fieldTemplatesResult[j].is_hidden,
                      id: fieldTemplatesResult[j].id,
                      process_reference_num: i,
                    })
                  }
                } else {
                  processDataRow.push({
                    value: '',
                    fieldType: FIELD_TYPES.LIST_MULTI,
                    is_hidden: fieldTemplatesResult[j].is_hidden,
                    id: fieldTemplatesResult[j].id,
                    process_reference_num: i,
                  })
                }
              }
            }
          } else if (fieldTemplatesResult[j].data_structure === FIELD_TYPES.TIMESTAMPED_BIGTEXT) {
            let value1 = processDataArr[i]['f_' + fieldTemplatesResult[j].id]
            if (!value1) {
              processDataRow.push({
                value: [],
                fieldType: fieldTemplatesResult[j].data_structure,
                is_hidden: fieldTemplatesResult[j].is_hidden,
                id: fieldTemplatesResult[j].id,
                process_reference_num: i,
              })
            } else {
              processDataRow.push({
                value: Array.isArray(value1) ? value1 : [],
                fieldType: fieldTemplatesResult[j].data_structure,
                is_hidden: fieldTemplatesResult[j].is_hidden,
                id: fieldTemplatesResult[j].id,
                process_reference_num: i,
              })
            }
          } else {
            let value1 = processDataArr[i]['f_' + fieldTemplatesResult[j].id]
            if (!value1) {
              processDataRow.push({
                value: '',
                fieldType: fieldTemplatesResult[j].data_structure,
                is_hidden: fieldTemplatesResult[j].is_hidden,
                id: fieldTemplatesResult[j].id,
                process_reference_num: i,
              })
            } else {
              processDataRow.push({
                value: value1.toString(),
                fieldType: fieldTemplatesResult[j].data_structure,
                is_hidden: fieldTemplatesResult[j].is_hidden,
                id: fieldTemplatesResult[j].id,
                process_reference_num: i,
              })
            }
          }
        }

        for (let activity of activityTemplates) {
          let activityData = getActivityData(
            i,
            processDataForProcessTemplate,
            activity.id,
            holidayList,
          )
          // version
          processDataRow.push({
            value: activityData['version'],
            id: activity.id,
            fieldType: 'number',
            is_hidden: false,
            process_reference_num: i,
            is_Activity: true,
          })
          // status
          processDataRow.push({
            value: activityData['status'],
            id: activity.id,
            fieldType: 'list',
            is_hidden: false,
            process_reference_num: i,
            is_Activity: true,
          })
          // activity assigned on
          processDataRow.push({
            value: activityData['assignedAt'],
            id: activity.id,
            fieldType: 'date',
            is_hidden: false,
            process_reference_num: i,
            is_Activity: true,
          })
          // closed date
          processDataRow.push({
            value: activityData['closedDate'],
            id: activity.id,
            fieldType: 'date',
            is_hidden: false,
            process_reference_num: i,
            is_Activity: true,
          })
          // assignee
          processDataRow.push({
            value: activityData['assignee'],
            id: activity.id,
            fieldType: 'list',
            is_hidden: false,
            process_reference_num: i,
            is_Activity: true,
          })
          // working hours
          processDataRow.push({
            value: activityData['workHours'],
            id: activity.id,
            fieldType: 'number',
            is_hidden: false,
            process_reference_num: i,
            is_Activity: true,
          })
        }
        processDataArr2.push(processDataRow)
      }
      setAggregatedProcessStatus(aggregatedProcessStatusLocal)
      setTotalTimeTaken(totalTimeTakenLocal)

      processDataArr2 = processDataArr2.reverse()
      for (let pdRow of processDataArr2) {
        pdRow.unshift({
          id: pdRow[0].id,
          value: pdRow[0].value.toString().split('-')[1],
          fieldType: null,
        })
      }

      const localConfig = getUserProcessDashboardConfig(processTemplateId)
      const hiddenFieldIds = localConfig?.hidden_field_ids || []
      const visibleActivityIds = localConfig?.visible_activity_template_ids || []

      const visibleColumnFields = fieldTemplatesResult?.filter(
        (it) => !hiddenFieldIds?.includes(it.id) && !it.is_hidden,
      )

      const visibleColumnActivity = activityTemplates?.filter((it) =>
        visibleActivityIds?.includes(it.id),
      )

      // actually hidden activity
      const hiddenActivityIds = activityTemplates
        .map((it) => it.id)
        .filter((it) => !visibleActivityIds?.includes(it))

      setProcessDashBoardData({
        processTemplate: processTemplateResult[0],
        processData: processDataArr2,
        fieldTemplatesAll: fieldTemplatesResult,
        fieldTemplates: visibleColumnFields,
        hiddenColumns: hiddenFieldIds,
        activityTemplatesAll: activityTemplates,
        activityTemplates: visibleColumnActivity,
        hiddenActivityColumns: hiddenActivityIds,
      })

      setSelectedActivity((prev) =>
        prev === null && activityTemplates.length > 0
          ? activityTemplates[0].activity_template_name
          : prev,
      )
    } catch (error) {
      console.error('Promise.all Error', error)
    }

    setIsLoading(false)
  }

  const handleSearchFieldsChange = (e) => {
    setSearchFields({
      ...searchFields,
      [e.target.name]: e.target.value,
    })
  }

  const openDialog = () => {
    setIsDialogOpen(true)
  }

  const exportToExcel = async () => {
    const dataToExport = isFiltersOn ? filteredProcessData : processData
    // First row of headers (Primary Headers)
    let columnIndex = 0
    let processMetaDataFrom = columnIndex
    let processMetaDataTo = processMetaDataFrom + (defaultTableHeaders.length - 1)

    columnIndex = processMetaDataTo
    let processDataFrom = columnIndex + 1
    let processDataTo = processDataFrom + (fieldTemplates.length - 1)

    columnIndex = processDataTo
    const primaryHeaders = [
      // Process Metadata
      {
        name: 'Process Metadata',
        from: `${EXCEL_COLUMNS[processMetaDataFrom]}1`,
        to: `${EXCEL_COLUMNS[processMetaDataTo]}1`,
      },
      // Process Data
      {
        name: 'Process Data',
        from: `${EXCEL_COLUMNS[processDataFrom]}1`,
        to: `${EXCEL_COLUMNS[processDataTo]}1`,
      },
    ]

    // Adding headers for activityTemplates based on defaultActivityTableHeaders
    activityTemplates.forEach((activity, index) => {
      const activityFrom = columnIndex + 1
      const activityTo = activityFrom + defaultActivityTableHeaders.length - 1

      primaryHeaders.push({
        name: `Timeline : ${activity.activity_template_name}`,
        from: `${EXCEL_COLUMNS[activityFrom]}1`,
        to: `${EXCEL_COLUMNS[activityTo]}1`,
      })

      columnIndex = activityTo
    })

    // Second row of headers (Secondary Headers)
    const secondaryHeaders = [
      ...defaultTableHeaders.map((it) => it.name),
      ...(fieldTemplates && fieldTemplates.length > 0
        ? fieldTemplates.map((ft) => ft.field_template_name)
        : []),
      ...(activityTemplates && activityTemplates.length > 0
        ? activityTemplates.flatMap(() => defaultActivityTableHeaders.map((header) => header.name))
        : []),
    ]

    setExcelLoading(true)

    // Create the primary header row with styles
    const header = {
      [EXCEL_COLUMNS[0]]: {
        v: '',
      },
    }

    // Create the secondary header row
    const secondaryHeader = secondaryHeaders.reduce((acc, cur, index) => {
      return {
        ...acc,
        [EXCEL_COLUMNS[index]]: {
          v: cur,
          s: {
            bold: true,
          },
        },
      }
    }, {})

    let allRowsData = [header, secondaryHeader]

    // Helper function to get the actual value based on field type
    const getActualValue = (fieldType, value, currencyCode) => {
      if (fieldType === FIELD_TYPES.DATE || fieldType === FIELD_TYPES.DATETIME) {
        let dateValue = value
        if (Array.isArray(dateValue)) {
          dateValue = dateValue.length > 0 ? dateValue[0] : ''
        }
        if (!dateValue || !moment(dateValue).isValid()) {
          return { v: '' }
        }
        const excelDate = moment(dateValue).toDate()
        return {
          v: excelDate,
          t: 'd',
          numFmt: fieldType === FIELD_TYPES.DATE ? FORMATS.DATE : FORMATS.DATETIME,
        }
      } else if (fieldType === FIELD_TYPES.CURRENCY) {
        const parsedValue = parseFloat(value)
        if (isNaN(parsedValue)) return { v: null }

        const currencySymbol = CURRENCY_COUNTRY_CODE[currencyCode] || ''
        const numFmt = CURRENCY_COUNTRY_CODE[currencyCode]
          ? `"${currencySymbol}" #,##0.00`
          : '"₹" #,##0.00'

        return { v: parsedValue, numFmt }
      } else if (fieldType === FIELD_TYPES.NUMBER) {
        const parsedValue = parseFloat(value)
        return { v: isNaN(parsedValue) ? null : parsedValue }
      } else if (fieldType === FIELD_TYPES.TIMESTAMPED_BIGTEXT) {
        if (Array.isArray(value) && value.length > 0) {
          const formattedValue = value
            .map((entry) => {
              const username = entry.user_id
                ? usersList
                    ?.find((u) => u.id === entry.user_id)
                    ?.value?.replace(/\s*\([^)]*\)\s*/g, '')
                    .trim()
                : null
              return `${entry.timestamp}: ${entry.text}${username ? ` - ${username}` : ''}`
            })
            .join('\n\n')

          return { v: formattedValue }
        }
        return { v: '' }
      } else if (fieldType === FIELD_TYPES.LIST || fieldType === FIELD_TYPES.LIST_MULTI) {
        // Join array values into a string, or just return the value if not an array
        if (Array.isArray(value)) {
          return { v: value.map(String).join(', ') }
        }
        return { v: value }
      } else {
        return { v: value }
      }
    }

    dataToExport.forEach((row) => {
      const filteredRow = row.filter((item) => {
        if (item.is_Activity) {
          return !hiddenActivityColumns?.includes(item.id)
        } else {
          return !hiddenColumns?.includes(item.id)
        }
      })
      const keys = Object.keys(filteredRow)
      const rest = keys.slice(1) // Exclude the first key
      const data = rest.reduce((acc, key, index) => {
        const currencyObj = currencyForDashboardValues.find(
          (currency) => currency.process_reference_num === filteredRow[key].process_reference_num,
        )

        const currencyCode = currencyObj ? currencyObj.currency_code : null

        return {
          ...acc,
          [EXCEL_COLUMNS[index]]: getActualValue(
            filteredRow[key].fieldType,
            filteredRow[key].value,
            currencyCode,
          ),
        }
      }, [])
      allRowsData.push(data)
    })

    // Set the filename for the Excel file
    const filename = `${processTemplate?.process_template_name}-(${moment(new Date()).format(
      'DD MMM YYYY',
    )})`

    // Download the Excel file
    downloadExcel(
      [
        {
          name: 'Sheet1',
          data: allRowsData,
        },
      ],
      filename,
      primaryHeaders,
    )

    setExcelLoading(false)
  }

  const handleSetHiddenColumns = async (hiddenFieldIds, hiddenActivityIds) => {
    if (user) {
      await saveUserProcessDashBoardConfigs(user, hiddenFieldIds, hiddenActivityIds)
    }
  }

  const saveUserProcessDashBoardConfigs = async (user, hiddenFieldIds, hiddenActivityIds) => {
    try {
      const visibleActivityIds = processDashBoardData.activityTemplatesAll
        ?.map((it) => it.id)
        ?.filter((it) => !hiddenActivityIds?.includes(it))

      updateFieldTemplate(hiddenFieldIds)
      updateActivityTemplate(hiddenActivityIds)

      const existingConfigs = JSON.parse(localStorage.getItem('processDashboardConfigs') || '[]')

      //  Always remove old config for this process
      let updatedConfigs = existingConfigs.filter(
        (item) => item.process_template_id !== processTemplateId,
      )

      //  Check if both empty
      const isHiddenEmpty = !hiddenFieldIds?.length
      const isVisibleEmpty = !visibleActivityIds?.length

      //  Only add if NOT empty
      if (!(isHiddenEmpty && isVisibleEmpty)) {
        updatedConfigs.push({
          process_template_id: processTemplateId,
          hidden_field_ids: hiddenFieldIds,
          visible_activity_template_ids: visibleActivityIds,
        })
      }

      //  Save cleaned data
      localStorage.setItem('processDashboardConfigs', JSON.stringify(updatedConfigs))
    } catch (error) {
      console.error(error)
    }
  }

  const updateFieldTemplate = (hiddenFieldIds = []) => {
    const newFieldTemplate = fieldTemplatesAll.filter(
      (it) => !hiddenFieldIds.includes(it.id) && !it.is_hidden,
    )
    setProcessDashBoardData((state) => ({
      ...state,
      fieldTemplates: newFieldTemplate,
      hiddenColumns: hiddenFieldIds,
    }))
  }

  const updateActivityTemplate = (hiddenActivityIds = []) => {
    const newActivityTemplate = activityTemplatesAll.filter(
      (it) => !hiddenActivityIds.includes(it.id),
    )
    setProcessDashBoardData((state) => ({
      ...state,
      activityTemplates: newActivityTemplate,
      hiddenActivityColumns: hiddenActivityIds,
    }))
  }

  const handleSetHiddenActivityColumns = async (hiddenActivityIds = []) => {
    if (user) {
      await saveUserProcessDashBoardConfigs(user, hiddenColumns, hiddenActivityIds)
    }
  }

  const handleShowFilters = () => {
    setShowFilterIcons((prevState) => !prevState)
    if (showFilterIcons) {
      setSearchFields({})
      setFilteredProcessData(processData)
    } else {
      if (
        !loadAllData &&
        totalProcessCreated >= processData.length &&
        totalProcessCreated > LOAD_ALL_DATA_LIMIT
      ) {
        setLoadAllDataWarningDialog(true)
      }
    }
  }
  const handleLoadAllData = async () => {
    setLoadAllDataWarningDialog(false)
    await getDataFromServer(0)
    setLoadAllData(true)
  }

  const renderFilterComponent = (index, dataStructure) => {
    if (!showFilterIcons) return null
    switch (dataStructure) {
      case FIELD_TYPES.BIGTEXT:
        return (
          <ClearableTextField
            id={'sf_' + index}
            name={'sf_' + index}
            variant="outlined"
            color="primary"
            size="small"
            onChange={handleSearchFieldsChange}
            fullWidth
            value={searchFields['sf_' + index] || ''}
            sx={{
              '& .MuiInputBase-root': {
                height: 25,
              },
            }}
            clear={clearFilterInput[index] || false}
          />
        )
      case FIELD_TYPES.TIMESTAMPED_BIGTEXT:
        return (
          <TextField
            id={'sf_' + index}
            name={'sf_' + index}
            variant="outlined"
            color="primary"
            size="small"
            onChange={handleSearchFieldsChange}
            fullWidth
            value={searchFields['sf_' + index] || ''}
            sx={{
              '& .MuiInputBase-root': {
                height: 25,
              },
            }}
            clear={clearFilterInput[index] || false}
          />
        )
      case FIELD_TYPES.TEXT:
        return (
          <ClearableTextField
            id={'sf_' + index}
            name={'sf_' + index}
            variant="outlined"
            color="primary"
            size="small"
            value={searchFields['sf_' + index] || ''}
            onChange={handleSearchFieldsChange}
            fullWidth
            sx={{
              '& .MuiInputBase-root': {
                height: 25,
              },
            }}
            clear={clearFilterInput[index] || false}
          />
        )
      case FIELD_TYPES.DATE:
        return (
          <DateRangePickerComponent
            id={'sf_' + index}
            onChange={(range) => {
              setSearchFields((prevState) => ({
                ...prevState,
                ['sf_' + index]: range,
              }))
            }}
            clear={clearFilterInput[index] || false}
            value={searchFields['sf_' + index]}
          />
        )
      case FIELD_TYPES.DATETIME:
        return (
          <DateTimeRangePickerComponent
            id={'sf_' + index}
            onChange={(range) => {
              setSearchFields((prevState) => ({
                ...prevState,
                ['sf_' + index]: range,
              }))
            }}
            clear={clearFilterInput[index] || false}
          />
        )

      case FIELD_TYPES.LIST_MULTI:
      case FIELD_TYPES.LIST: {
        const searchFieldsWithoutSelf = Object.fromEntries(
          Object.entries(searchFields).filter(([key]) => key !== 'sf_' + index),
        )
        const hasOtherFilters = Object.keys(searchFieldsWithoutSelf).length > 0

        const dataForCounts = (() => {
          if (!hasOtherFilters) return processData
          return processData.filter((pd) => {
            return Object.entries(searchFieldsWithoutSelf).every(([sfKey, sfValue]) => {
              const colIndex = Number(sfKey.split('_')[1])
              const cell = pd[colIndex + 1]
              if (!cell) return false
              if (cell.fieldType === FIELD_TYPES.CURRENCY) {
                const cellCurrencyObject = currencyForDashboardValues.find(
                  (currency) => currency.process_reference_num == cell.process_reference_num,
                )
                return containsKeys(cell.value, sfValue, cell.fieldType, cellCurrencyObject)
              } else if (Array.isArray(cell.value)) {
                return sfValue.some((searchVal) =>
                  cell.value.some((cellVal) => String(cellVal).trim() === String(searchVal).trim()),
                )
              } else {
                return containsKeys(cell.value, sfValue, cell.fieldType)
              }
            })
          })
        })()

        const options = processData
          .reduce((acc, pd) => {
            const cellValue = pd[index + 1]?.value
            if (!cellValue) return acc

            const values = Array.isArray(cellValue) ? cellValue : [cellValue]

            values.forEach((val) => {
              if (!val || typeof val === 'object') return
              const strVal = String(val).trim()
              if (!strVal) return

              const existingOption = acc.find((opt) => opt.value === strVal)
              if (!existingOption) {
                const activeCount = dataForCounts.filter((ad) => {
                  const adCellValue = ad[index + 1]?.value
                  if (!adCellValue) return false
                  if (Array.isArray(adCellValue)) {
                    return adCellValue.some((v) => String(v).trim() === strVal)
                  }
                  return String(adCellValue).trim() === strVal
                }).length
                acc.push({ value: strVal, count: activeCount })
              }
            })

            return acc
          }, [])
          .sort((a, b) => a.value.localeCompare(b.value))

        return (
          <ListDropdown
            options={options}
            onApply={(value) => {
              const selectedValues = value.map((item) => item.value.trim())
              setSearchFields((prevState) => {
                const newState = { ...prevState }
                if (selectedValues.length > 0) {
                  newState['sf_' + index] = selectedValues
                } else {
                  delete newState['sf_' + index]
                }
                return newState
              })
              setClearFilterInput((prev) => ({ ...prev, [index]: false }))
            }}
            clear={clearFilterInput[index] || false}
            value={searchFields['sf_' + index]}
          />
        )
      }
      case FIELD_TYPES.NUMBER:
        return (
          <NumberRangeComponent
            id={'sf_' + index}
            onChange={(range) => {
              const [initial, last] = range
              setSearchFields((prevState) => {
                const newState = { ...prevState }
                if (initial === null && last === null) {
                  delete newState['sf_' + index]
                } else {
                  newState['sf_' + index] = range
                }
                return newState
              })

              const updatedFilteredData = processData.filter((pd) => {
                const value = pd[index + 1]?.value
                return (initial === null || value >= initial) && (last === null || value <= last)
              })

              setFilteredProcessData(updatedFilteredData)
            }}
            clear={clearFilterInput[index] || false}
            value={searchFields['sf_' + index]}
          />
        )
      case FIELD_TYPES.CURRENCY:
        return (
          <CurrencyRangeComponent
            id={'sf_' + index}
            onChange={(filter) => {
              setSearchFields((prevState) => ({
                ...prevState,
                ['sf_' + index]: filter,
              }))
            }}
            clear={clearFilterInput[index] || false}
            value={searchFields['sf_' + index]}
          />
        )

      default:
        return null
    }
  }

  const isFilterApplied = (index) => {
    return Boolean(searchFields[`sf_${index}`])
  }

  const onClearFilter = (index) => {
    setClearFilterInput((prevState) => ({
      ...prevState,
      [index]: true,
    }))
    setSearchFields((prevState) => {
      const updatedFields = { ...prevState }
      delete updatedFields['sf_' + index]
      return updatedFields
    })

    setFilteredProcessData(processData)

    setTimeout(() => {
      setClearFilterInput((prev) => ({ ...prev, [index]: false }))
    }, 0)
  }

  const activityNamesArray = activityTemplates.map(
    (template) => `TimeLine: ${template.activity_template_name}`,
  )

  const prevScrollY = useRef(0)
  const handleScroll = () => {
    const table = tableRef.current

    if (table.scrollTop > prevScrollY.current) {
      if (
        table.scrollTop + table.clientHeight >= table.scrollHeight - 10 &&
        !loadAllData &&
        totalProcessCreated >= processData.length &&
        totalProcessCreated > LOAD_ALL_DATA_LIMIT
      ) {
        setLoadAllDataWarningDialog(true)
      }
    }
    prevScrollY.current = table.scrollTop
  }
  const filteredUsersForSelectedActivity = (() => {
    const dataToUse = isFiltersOn ? filteredProcessData : processData
    const activityIndex = activityTemplatesAll.findIndex(
      (a) => a.activity_template_name === selectedActivity,
    )

    if (activityIndex === -1) return []

    const baseIndex = 1 + defaultTableHeaders.length + fieldTemplatesAll.length + activityIndex * 6

    const users = new Set()
    dataToUse.forEach((row) => {
      if (
        row[baseIndex + 5] &&
        row[baseIndex + 5].value !== null &&
        row[baseIndex + 5].value !== undefined &&
        row[baseIndex + 5].value !== ''
      ) {
        const assignee = row[baseIndex + 4]?.value
        if (assignee && assignee !== '') {
          users.add(assignee)
        }
      }
    })
    return Array.from(users)
  })()

  const INFO_COLUMN_WIDTH = 50
  const SECOND_COLUMN_LEFT = INFO_COLUMN_WIDTH
  const resetAnalyticsFilters = () => {
    setSelectedActivity(activityTemplatesAll?.[0]?.activity_template_name || '')
    setSelectedUser(ASSIGNEE_DROPDOWN_DEFAULT_TEXT)
    setActivityStatType(TimeCalculationCategories.AVERAGE)
    setTooltipOpen(false)
  }

  return (
    <>
      <div className="process-dashbaord" style={{ marginTop: '-10px' }}>
        <div
          className="process-dashbaord"
          style={{ padding: '0px 5px 10px 0px', width: '100%', marginLeft: '0px' }}
        >
          {isLoading ? null : (
            <div
              className="process-group-icon"
              style={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                width: '100%',
                paddingLeft: '0px',
                paddingRight: '0px',
              }}
            >
              <Box
                style={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Box>
                  <div style={{ display: 'flex' }}>
                    <Typography
                      className="textPrimary fontWeight700 "
                      style={{ fontSize: dashboardTittle, fontWeight: 'bold' }}
                    >
                      {processTemplate.process_template_name}
                    </Typography>
                  </div>
                </Box>

                <Box
                  className="processdata-group-icon"
                  style={{
                    fontSize: '14px',
                  }}
                >
                  {
                    <div style={cardIcons}>
                      <div hidden={isCreateNewProcessButtonHidden}>
                        <Tooltip title="Initiate new process workflow">
                          <IconButton
                            disabled={isCreateNewProcessButtonDisabled}
                            onClick={openDialog}
                            size="large"
                            style={{ marginLeft: '5px', padding: '2px' }}
                          >
                            <AddOutlinedIcon sx={{ fontSize: '20px', color: '#41464a' }} />
                          </IconButton>
                        </Tooltip>
                      </div>
                      <Tooltip
                        title={
                          isFiltersOn
                            ? `Download Process Data in Excel format (${filteredProcessData.length} rows in current filtered view)`
                            : totalProcessCreated > processData.length
                              ? `Download Process Data in Excel format (showing ${processData.length} of ${totalProcessCreated} total records — load all data to export everything)`
                              : 'Download all Process Data in Excel format'
                        }
                      >
                        <IconButton
                          onClick={exportToExcel}
                          size="large"
                          style={{ margin: '0px  5px 0px 0px', padding: '2px' }}
                        >
                          {excelLoading ? (
                            <CircularProgress sx={{ fontSize: '20px', color: '#41464a' }} />
                          ) : (
                            <FileDownloadOutlinedIcon sx={{ fontSize: '20px', color: '#41464a' }} />
                          )}
                        </IconButton>
                      </Tooltip>

                      <TableSetting
                        columns={getFieldForSetting(fieldTemplatesAll)}
                        hiddenColumns={processDashBoardData.hiddenColumns}
                        setHiddenColumns={(it) =>
                          handleSetHiddenColumns(it, processDashBoardData.hiddenActivityColumns)
                        }
                        activityColumns={getColumnForSetting(activityTemplatesAll)}
                        hiddenActivityColumns={processDashBoardData.hiddenActivityColumns}
                        setHiddenActivityColumns={handleSetHiddenActivityColumns}
                        color="primary"
                      />
                      {!loadAllData ? (
                        <Tooltip title="Load all data to enable filters">
                          <IconButton
                            size="large"
                            onClick={() => setLoadAllDataWarningDialog(true)}
                            style={{ marginLeft: '5px', padding: '2px' }}
                          >
                            <Refresh sx={{ fontSize: '20px', color: '#41464a' }} />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title={showFilterIcons ? 'Clear All Filters' : 'Enable Filter'}>
                          <IconButton
                            size="large"
                            onClick={handleShowFilters}
                            style={{ marginLeft: '5px', padding: '2px' }}
                          >
                            {showFilterIcons ? (
                              <FilterAltOffOutlinedIcon
                                sx={{ fontSize: '20px', color: '#41464a' }}
                              />
                            ) : (
                              <FilterAltOutlinedIcon sx={{ fontSize: '20px', color: '#41464a' }} />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                    </div>
                  }
                </Box>
              </Box>

              {/* METRIC BOXES  */}
              <Box
                className="process-dashboard-metrices"
                sx={{
                  marginTop: '2px',
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'stretch',
                }}
              >
                {/* Task Details */}
                <Paper
                  elevation={2}
                  sx={{
                    p: '10px 12px',
                    borderRadius: '12px',
                    backgroundColor: '#f7f9ff',
                    flex: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '11px',
                      fontWeight: 600,
                      color: '#6b7280',
                    }}
                  >
                    Processes
                  </Typography>
                  <Box sx={{ display: 'flex', gap: '2px', alignItems: 'flex-start' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Box sx={{ display: 'flex', gap: '4px' }}>
                          <Typography sx={{ fontSize: '11px', color: '#6b7280' }}>Rows:</Typography>
                          <Typography sx={{ fontSize: '11px' }}>
                            <span style={{ fontWeight: 700 }}>
                              {isFiltersOn ? filteredProcessData.length : processData.length}
                            </span>
                            <span style={{ fontWeight: 400 }}> of {totalProcessCreated}</span>
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                        {/* Active */}
                        <Box sx={{ display: 'flex', gap: '4px' }}>
                          <Typography sx={{ fontSize: '11px', color: '#6b7280' }}>
                            Active:
                          </Typography>
                          <Typography sx={{ fontSize: '11px', fontWeight: 700 }}>
                            {
                              (isFiltersOn ? filteredProcessData : processData).filter(
                                (r) => r[2]?.value === PROCESS_STATUSES.ACTIVE,
                              ).length
                            }
                          </Typography>
                        </Box>

                        <Box
                          sx={{ display: 'flex', gap: '4px', position: 'relative', left: '62px' }}
                        >
                          <Typography sx={{ fontSize: '11px', color: '#6b7280' }}>
                            Closed:
                          </Typography>
                          <Typography sx={{ fontSize: '11px', fontWeight: 700 }}>
                            {
                              (isFiltersOn ? filteredProcessData : processData).filter(
                                (r) =>
                                  r[2]?.value === PROCESS_STATUSES.CLOSED_ACCEPTED ||
                                  r[2]?.value === PROCESS_STATUSES.CLOSED_REJECTED,
                              ).length
                            }
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <Select
                          value={timeStatType}
                          onChange={(e) => setTimeStatType(e.target.value)}
                          variant="standard"
                          disableUnderline
                          size="small"
                          sx={{
                            fontSize: '10px',
                            background: '#f3f4f6',
                            borderRadius: '6px',
                            height: '18px',
                            width: '50px',
                            minWidth: 'unset',
                            '& .MuiSelect-select': {
                              px: '6px',
                              py: '2px',
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              paddingRight: '20px !important',
                            },
                            '& .MuiSelect-icon': {
                              fontSize: '14px',
                              right: '2px',
                            },
                          }}
                        >
                          <MenuItem value={TimeCalculationCategories.AVERAGE}>Avg</MenuItem>
                          <MenuItem value={TimeCalculationCategories.MINIMUM}>Min</MenuItem>
                          <MenuItem value={TimeCalculationCategories.MAXIMUM}>Max</MenuItem>
                        </Select>

                        <Typography
                          sx={{
                            fontSize: '14px',
                            fontWeight: 700,
                            color: '#6c47e4',
                          }}
                        >
                          {(() => {
                            const data = isFiltersOn ? filteredProcessData : processData
                            const times = data
                              .map((r) => parseFloat(r[8]?.value))
                              .filter((v) => !isNaN(v) && v > 0)

                            if (!times.length) return '0 days'

                            let val
                            if (timeStatType === TimeCalculationCategories.MINIMUM)
                              val = Math.min(...times)
                            else if (timeStatType === TimeCalculationCategories.MAXIMUM)
                              val = Math.max(...times)
                            else val = times.reduce((a, b) => a + b, 0) / times.length

                            return `${val.toFixed(1)} wdays`
                          })()}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Paper>

                {/* Activity Details */}
                <Tooltip
                  open={tooltipOpen}
                  placement="top"
                  title={
                    isAnalyticsActive ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <span>Activity filters applied</span>
                        <FilterAltOffOutlinedIcon
                          onClick={(e) => {
                            e.stopPropagation()
                            resetAnalyticsFilters()
                          }}
                          sx={{ fontSize: '16px', cursor: 'pointer' }}
                        />
                      </Box>
                    ) : (
                      ''
                    )
                  }
                >
                  <div style={{ flex: 1, display: 'flex' }}>
                    <Paper
                      elevation={2}
                      sx={{
                        borderRadius: '10px',
                        padding: '10px 14px',
                        backgroundColor: isAnalyticsActive ? '#fff7ed' : '#f6f8fc',
                        border: isAnalyticsActive ? '1px solid #fb923c' : 'none',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                      }}
                    >
                      <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#6b7280' }}>
                        Tasks
                      </Typography>

                      <Box sx={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                          <Select
                            value={selectedActivity ?? ''}
                            onChange={(e) => {
                              setSelectedActivity(e.target.value)
                              setSelectedUser(ASSIGNEE_DROPDOWN_DEFAULT_TEXT)
                            }}
                            variant="standard"
                            disableUnderline
                            size="small"
                            displayEmpty
                            renderValue={(selected) => selected || 'Activities'}
                            sx={{
                              fontSize: '10px',
                              background: '#f3f4f6',
                              borderRadius: '6px',
                              height: '18px',
                              width: '100px',
                              '& .MuiSelect-select': {
                                px: '6px',
                                py: '2px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                paddingRight: '20px !important',
                              },
                              '& .MuiSelect-icon': {
                                fontSize: '14px',
                                right: '2px',
                              },
                            }}
                          >
                            {activityTemplatesAll.map((a) => (
                              <MenuItem key={a.id} value={a.activity_template_name}>
                                {a.activity_template_name}
                              </MenuItem>
                            ))}
                          </Select>

                          <Select
                            value={selectedUser}
                            onChange={(e) => setSelectedUser(e.target.value)}
                            variant="standard"
                            disableUnderline
                            size="small"
                            displayEmpty
                            renderValue={(selected) => selected || 'Assignees'}
                            sx={{
                              fontSize: '10px',
                              background: '#f3f4f6',
                              borderRadius: '6px',
                              height: '18px',
                              width: '100px',
                              '& .MuiSelect-select': {
                                px: '6px',
                                py: '2px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                paddingRight: '20px !important',
                              },
                              '& .MuiSelect-icon': {
                                fontSize: '14px',
                                right: '2px',
                              },
                            }}
                          >
                            <MenuItem value={ASSIGNEE_DROPDOWN_DEFAULT_TEXT}>
                              All Assignees
                            </MenuItem>
                            {filteredUsersForSelectedActivity.map((u) => (
                              <MenuItem key={u} value={u}>
                                {u}
                              </MenuItem>
                            ))}
                          </Select>
                        </Box>

                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: '4px',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Select
                              value={activityStatType}
                              onChange={(e) => setActivityStatType(e.target.value)}
                              variant="standard"
                              disableUnderline
                              size="small"
                              sx={{
                                fontSize: '10px',
                                background: '#f3f4f6',
                                borderRadius: '6px',
                                height: '18px',
                                width: '50px',
                                minWidth: 'unset',
                                '& .MuiSelect-select': {
                                  px: '6px',
                                  py: '2px',
                                  display: 'block',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  paddingRight: '20px !important',
                                },
                                '& .MuiSelect-icon': {
                                  fontSize: '14px',
                                  right: '2px',
                                },
                              }}
                            >
                              <MenuItem value={TimeCalculationCategories.AVERAGE}>Avg</MenuItem>
                              <MenuItem value={TimeCalculationCategories.MINIMUM}>Min</MenuItem>
                              <MenuItem value={TimeCalculationCategories.MAXIMUM}>Max</MenuItem>
                            </Select>

                            <Typography
                              sx={{ fontSize: '14px', fontWeight: 700, color: '#6c47e4' }}
                            >
                              {(() => {
                                const data = isFiltersOn ? filteredProcessData : processData
                                const actIdx = activityTemplatesAll.findIndex(
                                  (a) => a.activity_template_name === selectedActivity,
                                )
                                if (actIdx === -1) return 'N/A'

                                const base =
                                  1 +
                                  defaultTableHeaders.length +
                                  fieldTemplatesAll.length +
                                  actIdx * 6
                                const whIdx = base + 5
                                const asIdx = base + 4

                                let rows = data
                                if (selectedUser !== ASSIGNEE_DROPDOWN_DEFAULT_TEXT) {
                                  rows = data.filter((r) => {
                                    const c = r[asIdx]
                                    if (!c) return false
                                    return Array.isArray(c.value)
                                      ? c.value.includes(selectedUser)
                                      : c.value === selectedUser
                                  })
                                }

                                const times = rows
                                  .map((r) => parseFloat(r[whIdx]?.value))
                                  .filter((v) => !isNaN(v) && v >= 0)

                                if (!times.length) return 'N/A'

                                let h
                                if (activityStatType === TimeCalculationCategories.MINIMUM)
                                  h = Math.min(...times)
                                else if (activityStatType === TimeCalculationCategories.MAXIMUM)
                                  h = Math.max(...times)
                                else h = times.reduce((a, b) => a + b, 0) / times.length

                                return `${(h / 8.5).toFixed(1)} wdays`
                              })()}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Typography sx={{ fontSize: '11px', color: '#6b7280' }}>
                              Affected Records:
                            </Typography>
                            <Typography sx={{ fontSize: '11px', fontWeight: 700, lineHeight: 1.2 }}>
                              {(() => {
                                const dataToUse = isFiltersOn ? filteredProcessData : processData
                                const activityIndex = activityTemplatesAll.findIndex(
                                  (a) => a.activity_template_name === selectedActivity,
                                )
                                if (activityIndex === -1) return ''

                                const baseIndex =
                                  1 +
                                  defaultTableHeaders.length +
                                  fieldTemplatesAll.length +
                                  activityIndex * 6
                                const workHoursIdx = baseIndex + 5
                                const assigneeIdx = baseIndex + 4

                                let rows = dataToUse
                                if (selectedUser !== ASSIGNEE_DROPDOWN_DEFAULT_TEXT) {
                                  rows = dataToUse.filter((row) => {
                                    const cell = row[assigneeIdx]
                                    if (!cell) return false
                                    return Array.isArray(cell.value)
                                      ? cell.value.includes(selectedUser)
                                      : cell.value === selectedUser
                                  })
                                }

                                const times = rows
                                  .map((row) => parseFloat(row[workHoursIdx]?.value))
                                  .filter((val) => !isNaN(val) && val >= 0)

                                return `${times.length}`
                              })()}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Paper>
                  </div>
                </Tooltip>
              </Box>
              {/* METRIC BOXES */}
            </div>
          )}
        </div>

        {isDialogOpen && (
          <ConfirmationBox
            title={confirmationText}
            isDialogOpen={isDialogOpen}
            setIsDialogOpen={setIsDialogOpen}
            onYes={handleCreateNewProcess}
          />
        )}

        {isLoading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '70vh',
            }}
          >
            <CircularProgress color="primary" size={50} />
          </div>
        ) : (
          <>
            <TableContainer
              sx={{
                width: '100%',
                overflowX: 'auto',
                maxHeight: '75vh',
                position: 'relative',
              }}
              component={Paper}
              ref={tableRef}
              onScroll={handleScroll}
            >
              <Table className="responsiveTable" stickyHeader aria-label="simple table">
                <TableHead ref={tableHeadRef}>
                  <TableRow sx={{ position: 'sticky', zIndex: 3, top: 0 }}>
                    <TableCell
                      align="center"
                      sx={{ backgroundColor: grayHeaderColor }}
                      colSpan={defaultTableHeaders.length + 1}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 'bolder',
                          letterSpacing: '1px',
                          fontSize: '16px',
                          color: '#6c47e4',
                        }}
                      >
                        Process MetaData
                      </Typography>
                    </TableCell>

                    {fieldTemplates?.length > 0 && (
                      <TableCell
                        className=""
                        align="center"
                        sx={{
                          backgroundColor: grayHeaderColor,
                          fontWeight: 'bolder',
                          letterSpacing: '4px',
                          fontSize: '16px',
                        }}
                        colSpan={fieldTemplates.length}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 'bolder',
                            letterSpacing: '1px',
                            fontSize: '16px',
                            color: '#6c47e4',
                          }}
                        >
                          Process Data
                        </Typography>
                      </TableCell>
                    )}
                    {activityNamesArray.map((name, index) => (
                      <TableCell
                        className=""
                        key={index}
                        align="center"
                        sx={{ backgroundColor: grayHeaderColor }}
                        colSpan={defaultActivityTableHeaders.length}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 'bolder',
                            letterSpacing: '4px',
                            fontSize: '16px',
                            color: '#6c47e4',
                          }}
                        >
                          {name}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow sx={{ position: 'sticky', zIndex: 3, top: secondHeaderTop }}>
                    <TableCell
                      align="left"
                      sx={{
                        width: INFO_COLUMN_WIDTH,
                        minWidth: INFO_COLUMN_WIDTH,
                        position: 'sticky',
                        left: 0,
                        zIndex: 5,
                        backgroundColor: grayHeaderColor,
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                        Info
                      </Typography>
                    </TableCell>

                    {defaultTableHeaders.map((header, i) => (
                      <TableCell
                        key={i}
                        align="center"
                        sx={{
                          minWidth: header.width,
                          backgroundColor: grayHeaderColor,

                          ...(i === 0 && {
                            position: 'sticky',
                            left: SECOND_COLUMN_LEFT,
                            zIndex: 5,
                          }),
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="center">
                          <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                            {header.name}
                          </Typography>

                          {showFilterIcons && (
                            <Tooltip title={isFilterApplied(i) ? 'Clear Filters' : null}>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  if (isFilterApplied(i)) {
                                    onClearFilter(i)
                                  }
                                }}
                              >
                                {isFilterApplied(i) ? (
                                  <FilterAltOffOutlinedIcon sx={{ fontSize: '18px' }} />
                                ) : (
                                  <FilterAltOutlinedIcon sx={{ fontSize: '18px' }} />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    ))}

                    {fieldTemplatesAll?.length > 0 &&
                      fieldTemplatesAll.map(
                        (f, i) =>
                          !f?.is_hidden &&
                          !hiddenColumns?.includes(f.id) && (
                            <TableCell
                              className=""
                              id={i + defaultTableHeaders.length}
                              key={i + defaultTableHeaders.length}
                              align="center"
                              sx={{
                                minWidth: `${f.field_width_in_rem_for_process_dashboard}rem`,
                                backgroundColor: grayHeaderColor,
                              }}
                            >
                              <Box display="flex" alignItems="center" justifyContent="center">
                                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                  {f?.field_template_name}
                                </Typography>

                                {showFilterIcons && (
                                  <Tooltip
                                    title={
                                      isFilterApplied(i + defaultTableHeaders.length)
                                        ? 'Filter Applied'
                                        : 'Enable Filter'
                                    }
                                  >
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        if (isFilterApplied(i + defaultTableHeaders.length)) {
                                          onClearFilter(i + defaultTableHeaders.length)
                                        }
                                      }}
                                    >
                                      {isFilterApplied(i + defaultTableHeaders.length) ? (
                                        <FilterAltOffOutlinedIcon sx={{ fontSize: '18px' }} />
                                      ) : (
                                        <FilterAltOutlinedIcon sx={{ fontSize: '18px' }} />
                                      )}
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                          ),
                      )}

                    {(() => {
                      let visibleActivityIndex = 0
                      return activityTemplatesAll.map((data) => {
                        if (hiddenActivityColumns?.includes(data.id)) return null
                        const currentActivityIndex = visibleActivityIndex
                        visibleActivityIndex++
                        return defaultActivityTableHeaders.map((header, i) => {
                          const filterIndex =
                            i +
                            defaultTableHeaders.length +
                            fieldTemplatesAll.length +
                            currentActivityIndex * defaultActivityTableHeaders.length
                          return (
                            <TableCell
                              className=""
                              key={`${data.id}-${i}`}
                              align={getAlignmentForType(header.data_structure)}
                              sx={{ minWidth: header.width, backgroundColor: grayHeaderColor }}
                            >
                              <Box display="flex" alignItems="center" justifyContent="center">
                                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                  {header.name}
                                </Typography>
                                {showFilterIcons && (
                                  <Tooltip
                                    title={
                                      isFilterApplied(filterIndex)
                                        ? 'Filter Applied'
                                        : 'Enable Filter'
                                    }
                                  >
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        if (isFilterApplied(filterIndex)) {
                                          onClearFilter(filterIndex)
                                        }
                                      }}
                                    >
                                      {isFilterApplied(filterIndex) ? (
                                        <FilterAltOffOutlinedIcon sx={{ fontSize: '18px' }} />
                                      ) : (
                                        <FilterAltOutlinedIcon sx={{ fontSize: '18px' }} />
                                      )}
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                          )
                        })
                      })
                    })()}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {showFilterIcons && (
                    <TableRow
                      style={{
                        position: 'sticky',
                        top: filterRowTop - 1,
                        backgroundColor: '#fff',
                        zIndex: 3,
                      }}
                    >
                      <TableCell
                        style={{
                          padding: '8px',
                          width: INFO_COLUMN_WIDTH,
                          minWidth: INFO_COLUMN_WIDTH,
                          position: 'sticky',
                          left: 0,
                          backgroundColor: '#fff',
                          zIndex: 5,
                        }}
                      />

                      {defaultTableHeaders.map((header, index) => (
                        <TableCell
                          key={index}
                          style={{
                            padding: '8px',

                            ...(index === 0 && {
                              position: 'sticky',
                              left: SECOND_COLUMN_LEFT,
                              backgroundColor: '#fff',
                              zIndex: 5,
                            }),
                          }}
                        >
                          {renderFilterComponent(index, header.data_structure)}
                        </TableCell>
                      ))}

                      {fieldTemplatesAll?.length > 0 &&
                        fieldTemplatesAll.map(
                          (f, i) =>
                            !f.is_hidden &&
                            !hiddenColumns?.includes(f.id) && (
                              <TableCell
                                id={'sf_' + (i + defaultTableHeaders.length)}
                                key={i + defaultTableHeaders.length}
                                style={{ padding: '8px' }}
                              >
                                {renderFilterComponent(
                                  i + defaultTableHeaders.length,
                                  f.data_structure,
                                )}
                              </TableCell>
                            ),
                        )}

                      {(() => {
                        let visibleActivityIndex = 0
                        return (
                          activityTemplatesAll?.length > 0 &&
                          activityTemplatesAll.map((data) => {
                            if (hiddenActivityColumns?.includes(data.id)) return null
                            const currentActivityIndex = visibleActivityIndex
                            visibleActivityIndex++
                            return defaultActivityTableHeaders.map((header, i) => {
                              const filterIndex =
                                i +
                                defaultTableHeaders.length +
                                fieldTemplatesAll.length +
                                currentActivityIndex * defaultActivityTableHeaders.length
                              return (
                                <TableCell
                                  key={`${data.id}-${i}`}
                                  align={getAlignmentForType(header.data_structure)}
                                  style={{ padding: '8px' }}
                                >
                                  {renderFilterComponent(filterIndex, header.data_structure)}
                                </TableCell>
                              )
                            })
                          })
                        )
                      })()}
                    </TableRow>
                  )}

                  {isFiltersOn ? (
                    <ProcessDashboardValues
                      processData={filteredProcessData}
                      process_template_id={processTemplate.id}
                      hiddenColumns={hiddenColumns}
                      currencyForDashboardValues={currencyForDashboardValues}
                      hiddenActivityColumns={hiddenActivityColumns}
                      processTemplates={processTemplate}
                      processDataForProcessTemplate={processDataForProcessTemplate}
                      companyDetails={companyDetails}
                      usersList={usersList}
                    />
                  ) : (
                    <ProcessDashboardValues
                      processData={processData}
                      process_template_id={processTemplate.id}
                      hiddenColumns={hiddenColumns}
                      currencyForDashboardValues={currencyForDashboardValues}
                      hiddenActivityColumns={hiddenActivityColumns}
                      processTemplates={processTemplate}
                      processDataForProcessTemplate={processDataForProcessTemplate}
                      companyDetails={companyDetails}
                      usersList={usersList}
                    />
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
        {loadAllDataWarningDialog && (
          <ConfirmationBox
            isDialogOpen={loadAllDataWarningDialog}
            setIsDialogOpen={setLoadAllDataWarningDialog}
            title={'Load all data? This could take a while.'}
            onYes={handleLoadAllData}
          />
        )}
      </div>
    </>
  )
}

export default ProcessDashboard
